import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { createConfigTester } from '../../../api/configTesters.js'
import { listAbTests } from '../../../api/abTests.js'
import { listGroupConfigs, listGroupConfigVersions } from '../../../api/groupConfigs.js'
import { listSegmentations } from '../../../api/segmentations.js'
import { USER_PARAM_TYPES, formatVariantLabel } from './configTesterFormUtils.js'

async function loadRemoteConfigs(applicationId) {
  const groupConfigs = await listGroupConfigs(applicationId)
  if (!Array.isArray(groupConfigs) || groupConfigs.length === 0) return []
  const versionGroups = await Promise.all(
    groupConfigs.map((gc) => listGroupConfigVersions(applicationId, gc.id)),
  )
  return versionGroups.flat().sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
}

let idSeq = 0
function nextId(prefix) {
  idSeq += 1
  return `${prefix}_${idSeq}_${Date.now()}`
}

export default function ConfigTesterCreatePage() {
  const { applicationId } = useOutletContext()
  const navigate = useNavigate()

  const [refs, setRefs] = useState({
    status: 'loading',
    remoteConfigs: [],
    segmentations: [],
    abTests: [],
    error: null,
  })
  const [values, setValues] = useState({
    qaId: '',
    remoteConfigId: '',
    segmentationId: '',
    description: '',
    expire: '25920000',
  })
  const [abTestRows, setAbTestRows] = useState([])
  const [userParamRows, setUserParamRows] = useState([])
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      loadRemoteConfigs(applicationId),
      listSegmentations(applicationId, { includeDefaults: true }),
      listAbTests(applicationId).catch(() => []),
    ])
      .then(([remoteConfigs, segmentations, abTests]) => {
        if (cancelled) return
        setRefs({
          status: 'ok',
          remoteConfigs,
          segmentations,
          abTests: Array.isArray(abTests) ? abTests : [],
          error: null,
        })
      })
      .catch((loadError) => {
        if (cancelled) return
        setRefs({ status: 'error', remoteConfigs: [], segmentations: [], abTests: [], error: loadError })
      })
    return () => { cancelled = true }
  }, [applicationId])

  const availableSegmentations = useMemo(() => {
    const rc = refs.remoteConfigs.find((r) => String(r.id) === String(values.remoteConfigId))
    if (!rc?.groupConfigId) return refs.segmentations
    return refs.segmentations.filter(
      (s) => !s.groupConfigId || String(s.groupConfigId) === String(rc.groupConfigId),
    )
  }, [refs, values.remoteConfigId])

  function onChange(field) {
    return (e) => {
      const v = e.target.value
      setValues((prev) => {
        const next = { ...prev, [field]: v }
        if (field === 'remoteConfigId') next.segmentationId = ''
        return next
      })
    }
  }

  function addAbTestRow() {
    setAbTestRows((prev) => [...prev, { _id: nextId('ab'), abTestId: '', abVariantId: '' }])
  }
  function removeAbTestRow(rowId) {
    setAbTestRows((prev) => prev.filter((r) => r._id !== rowId))
  }
  function changeAbTestRow(rowId, field, value) {
    setAbTestRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r
        const next = { ...r, [field]: value }
        if (field === 'abTestId') next.abVariantId = ''
        return next
      }),
    )
  }

  function addUserParamRow() {
    setUserParamRows((prev) => [...prev, { _id: nextId('up'), paramType: '', paramValue: '' }])
  }
  function removeUserParamRow(rowId) {
    setUserParamRows((prev) => prev.filter((r) => r._id !== rowId))
  }
  function changeUserParamRow(rowId, field, value) {
    setUserParamRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, [field]: value } : r)))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (isSubmitting) return
    setError(null)
    if (!String(values.qaId || '').trim()) { setError(new Error('QA ID is required')); return }
    if (!values.remoteConfigId) { setError(new Error('Remote config is required')); return }

    try {
      setIsSubmitting(true)
      const created = await createConfigTester(applicationId, {
        qaId: values.qaId,
        remoteConfigId: values.remoteConfigId,
        segmentationId: values.segmentationId || null,
        description: values.description,
        expire: values.expire,
        abTests: abTestRows.filter((r) => r.abTestId && r.abVariantId),
        userParams: userParamRows.filter((r) => r.paramType && r.paramValue),
      })
      if (created?.id) {
        navigate(`/applications/${applicationId}/config-tester/${created.id}`)
      } else {
        navigate(`/applications/${applicationId}/config-tester`)
      }
    } catch (err) {
      const detail = err?.body?.detail
      setError(new Error(detail ? String(detail) : String(err?.message || err)))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (refs.status === 'loading') {
    return <section className="card sectionCard"><div className="cardTitle">Loading...</div></section>
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Create config tester</h1>
        <Link to={`/applications/${applicationId}/config-tester`} className="button buttonGhost">Back</Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">Config tester</div>
        <form className="formGrid" onSubmit={handleSubmit}>
          <label className="formField">
            <span className="formLabel">QA ID (Appsflyer)</span>
            <input className="textInput" value={values.qaId} onChange={onChange('qaId')} placeholder="e.g. 12345-67890" />
          </label>

          <label className="formField">
            <span className="formLabel">Remote config</span>
            <select className="textInput" value={values.remoteConfigId} onChange={onChange('remoteConfigId')}>
              <option value="">Choose remote config</option>
              {refs.remoteConfigs.map((rc) => (
                <option key={rc.id} value={String(rc.id)}>
                  {rc.bundleVersion || rc.id} ({rc.status || 'Draft'})
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Segmentation</span>
            <select className="textInput" value={values.segmentationId} onChange={onChange('segmentationId')}>
              <option value="">Without segmentation</option>
              {availableSegmentations.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name || s.id}</option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Expire (seconds)</span>
            <input className="textInput" type="number" min="1" value={values.expire} onChange={onChange('expire')} />
          </label>

          <label className="formField formFieldFullWidth">
            <span className="formLabel">Description</span>
            <textarea className="textInput textAreaMin90" value={values.description} onChange={onChange('description')} placeholder="Optional description" />
          </label>

          <div className="formField formFieldFullWidth">
            <span className="formLabel">AB tests (max 3)</span>
            {abTestRows.map((row) => {
              const selectedTest = refs.abTests.find((t) => String(t.id) === String(row.abTestId))
              const variants = selectedTest?.variants || []
              return (
                <div key={row._id} className="builderRuleGridWide sectionTop10">
                  <select className="textInput" value={row.abTestId} onChange={(e) => changeAbTestRow(row._id, 'abTestId', e.target.value)}>
                    <option value="">Select AB test</option>
                    {refs.abTests.map((t) => (
                      <option key={t.id} value={String(t.id)}>{t.name || t.id}</option>
                    ))}
                  </select>
                  <select className="textInput" value={row.abVariantId} onChange={(e) => changeAbTestRow(row._id, 'abVariantId', e.target.value)}>
                    <option value="">Select variant</option>
                    {variants.map((v) => (
                      <option key={v.id} value={String(v.id)}>{formatVariantLabel(v)}</option>
                    ))}
                  </select>
                  <button type="button" className="button buttonGhost buttonDanger" onClick={() => removeAbTestRow(row._id)}>Remove</button>
                </div>
              )
            })}
            {abTestRows.length < 3 ? (
              <div className="sectionTop10">
                <button type="button" className="button buttonGhost" onClick={addAbTestRow}>Add AB test</button>
              </div>
            ) : null}
          </div>

          <div className="formField formFieldFullWidth">
            <span className="formLabel">User parameters (max 5)</span>
            {userParamRows.map((row) => (
              <div key={row._id} className="builderRuleGridWide sectionTop10">
                <select className="textInput" value={row.paramType} onChange={(e) => changeUserParamRow(row._id, 'paramType', e.target.value)}>
                  <option value="">Select param</option>
                  {USER_PARAM_TYPES.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
                <input className="textInput" value={row.paramValue} onChange={(e) => changeUserParamRow(row._id, 'paramValue', e.target.value)} placeholder="Value" />
                <button type="button" className="button buttonGhost buttonDanger" onClick={() => removeUserParamRow(row._id)}>Remove</button>
              </div>
            ))}
            {userParamRows.length < 5 ? (
              <div className="sectionTop10">
                <button type="button" className="button buttonGhost" onClick={addUserParamRow}>Add parameter</button>
              </div>
            ) : null}
          </div>

          <div className="formActions">
            <button type="submit" className="button buttonPrimary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
        {error ? <div className="formError">{String(error?.message || error)}</div> : null}
      </section>
    </div>
  )
}
